import { useBookDemo } from '../components/BookDemoContext'
import Navbar from '../components/Navbar'
import Footer from '../components/Footer'
import './HowItWorks.css'

export default function HowItWorks() {
  const openBookDemo = useBookDemo()
  return (
    <>
      <Navbar />

      {/* ── Hero ── */}
      <section className="hiw-hero">
        <div className="container">
          <h1>How PossoBuild Works</h1>
          <p>A proven 90-day enterprise implementation that delivers measurable ROI — from pilot to full platform.</p>
          <div className="hero-cta">
            <button className="btn-primary" onClick={() => openBookDemo('Book a Demo')}>Book a Demo</button>
            <a href="/product-tour" className="btn-outline">See Product Tour</a>
          </div>
        </div>
      </section>

      {/* ── Process Overview ── */}
      <section className="process-overview">
        <div className="container">
          <h2>90-Day Enterprise Implementation</h2>
          <p className="process-overview__sub">Three structured phases designed to minimise risk and maximise adoption</p>
          <div className="process-cards">
            <div className="process-card">
              <div className="process-card__days">30 Days</div>
              <div className="process-card__title">Pilot &amp; Prove</div>
              <div className="process-card__desc">Launch SmartHire for one role type and demonstrate clear ROI before scaling</div>
            </div>
            <div className="process-arrow">→</div>
            <div className="process-card">
              <div className="process-card__days">60 Days</div>
              <div className="process-card__title">Optimize &amp; Expand</div>
              <div className="process-card__desc">Refine based on data, integrate with your ATS, and expand to more roles and teams</div>
            </div>
            <div className="process-arrow">→</div>
            <div className="process-card">
              <div className="process-card__days">90 Days</div>
              <div className="process-card__title">Scale &amp; Transform</div>
              <div className="process-card__desc">Roll out across the organisation with SmartDevelop, SmartAssess, and SmartAssist</div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Phase 1: Days 1–30 ── */}
      <section className="phase-section">
        <div className="container">
          <div className="phase-content">
            <div className="phase-text">
              <div className="phase-label">Phase 1 · Days 1–30</div>
              <h2>Pilot &amp; Prove ROI</h2>
              <p>
                Start with a focused pilot on your highest-volume or most critical role. In 30 days you'll have proven ROI data, a process your team loves, and the confidence to scale.
              </p>
              <ul className="phase-checklist">
                <li>Kickoff workshop with your HR and Talent Acquisition team</li>
                <li>Configure SmartHire for your target role — custom questions, scoring rubrics, pass criteria</li>
                <li>Train your team on the platform in under 2 hours</li>
                <li>Run parallel process alongside your existing workflow to compare results</li>
                <li>Weekly check-ins with your dedicated Customer Success Manager</li>
              </ul>
            </div>

            <div>
              <div className="results-highlight" style={{marginBottom:'1.5rem'}}>
                <div className="results-highlight__title">30-Day Results</div>
                <div className="results-highlight__items">
                  <div className="result-item">
                    <div className="result-item__icon">⚡</div>
                    <div className="result-item__body">
                      <div className="result-item__value">70%+</div>
                      <div className="result-item__desc">Reduction in recruiter screening time</div>
                    </div>
                  </div>
                  <div className="result-item">
                    <div className="result-item__icon">📊</div>
                    <div className="result-item__body">
                      <div className="result-item__value">100%</div>
                      <div className="result-item__desc">Candidates receive a structured scorecard</div>
                    </div>
                  </div>
                  <div className="result-item">
                    <div className="result-item__icon">🎯</div>
                    <div className="result-item__body">
                      <div className="result-item__value">Day 1</div>
                      <div className="result-item__desc">Skills intelligence available from first interview</div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="phase-visual">
                <div className="phase-visual__days">30 Days</div>
                <div className="phase-visual__label">To Proven ROI</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Phase 2: Days 31–60 ── */}
      <section className="phase-section">
        <div className="container">
          <div className="phase-content">
            <div className="phase-text">
              <div className="phase-label">Phase 2 · Days 31–60</div>
              <h2>Optimize &amp; Expand</h2>
              <p>
                With pilot data in hand, you refine the platform, integrate it into your existing tech stack, and expand SmartHire to new roles — while introducing SmartDevelop for your existing team.
              </p>
              <ul className="phase-checklist">
                <li>Analyse pilot data to refine interview questions and scoring criteria</li>
                <li>ATS and HRIS integration for seamless workflow</li>
                <li>Expand SmartHire to 5–10 additional role types</li>
                <li>Introduce SmartDevelop for career pathway planning</li>
                <li>Manager training on using AI-generated insights for coaching</li>
              </ul>
            </div>

            <div>
              <div className="results-highlight" style={{marginBottom:'1.5rem'}}>
                <div className="results-highlight__title">60-Day Results</div>
                <div className="results-highlight__items">
                  <div className="result-item">
                    <div className="result-item__icon">🚀</div>
                    <div className="result-item__body">
                      <div className="result-item__value">10×</div>
                      <div className="result-item__desc">Interview throughput vs. traditional process</div>
                    </div>
                  </div>
                  <div className="result-item">
                    <div className="result-item__icon">🔗</div>
                    <div className="result-item__body">
                      <div className="result-item__value">Seamless</div>
                      <div className="result-item__desc">Integrated with your existing ATS and HRIS</div>
                    </div>
                  </div>
                  <div className="result-item">
                    <div className="result-item__icon">📈</div>
                    <div className="result-item__body">
                      <div className="result-item__value">Live</div>
                      <div className="result-item__desc">Career development roadmaps for every employee</div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="phase-visual">
                <div className="phase-visual__days">60 Days</div>
                <div className="phase-visual__label">To Full Expansion</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Phase 3: Days 61–90 ── */}
      <section className="phase-section">
        <div className="container">
          <div className="phase-content">
            <div className="phase-text">
              <div className="phase-label">Phase 3 · Days 61–90</div>
              <h2>Scale &amp; Transform</h2>
              <p>
                The full PossoBuild platform is live across your organisation. SmartAssess verifies training outcomes, SmartAssist handles HR queries at scale, and your team has a unified talent intelligence dashboard.
              </p>
              <ul className="phase-checklist">
                <li>Org-wide rollout of SmartHire across all hiring teams</li>
                <li>All-hands platform training and change management support</li>
                <li>SmartAssess launch for post-training skill verification</li>
                <li>SmartAssist configured with your policy library and HR knowledge base</li>
                <li>Executive success dashboard showing ROI across all modules</li>
                <li>90-day business review and roadmap for year 2</li>
              </ul>
            </div>

            <div>
              <div className="results-highlight" style={{marginBottom:'1.5rem'}}>
                <div className="results-highlight__title">90-Day Results</div>
                <div className="results-highlight__items">
                  <div className="result-item">
                    <div className="result-item__icon">🏆</div>
                    <div className="result-item__body">
                      <div className="result-item__value">Full</div>
                      <div className="result-item__desc">Platform live across hiring, development, and support</div>
                    </div>
                  </div>
                  <div className="result-item">
                    <div className="result-item__icon">🔄</div>
                    <div className="result-item__body">
                      <div className="result-item__value">Active</div>
                      <div className="result-item__desc">Data flywheel improving every hire and review</div>
                    </div>
                  </div>
                  <div className="result-item">
                    <div className="result-item__icon">💡</div>
                    <div className="result-item__body">
                      <div className="result-item__value">Strategic</div>
                      <div className="result-item__desc">HR transformed from admin function to strategic partner</div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="phase-visual">
                <div className="phase-visual__days">90 Days</div>
                <div className="phase-visual__label">To Full Platform</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Success Metrics ── */}
      <section className="metrics-section">
        <div className="container">
          <h2>Measurable Success Metrics</h2>
          <p className="metrics-section__sub">Every deployment is tracked against these benchmarks</p>
          <div className="metrics-grid">
            <div className="metric-card">
              <div className="metric-card__icon">⏱️</div>
              <div className="metric-card__value">70%</div>
              <div className="metric-card__label">Time Savings</div>
              <div className="metric-card__desc">Reduction in recruiter screening and interview coordination time</div>
            </div>
            <div className="metric-card">
              <div className="metric-card__icon">💰</div>
              <div className="metric-card__value">60%</div>
              <div className="metric-card__label">Cost Reduction</div>
              <div className="metric-card__desc">Lower cost-per-hire through AI-first screening and consistent evaluation</div>
            </div>
            <div className="metric-card">
              <div className="metric-card__icon">📊</div>
              <div className="metric-card__value">40%</div>
              <div className="metric-card__label">Quality Improvement</div>
              <div className="metric-card__desc">Increase in 90-day retention for AI-assessed hires vs. traditional process</div>
            </div>
            <div className="metric-card">
              <div className="metric-card__icon">🎯</div>
              <div className="metric-card__value">95%</div>
              <div className="metric-card__label">Platform Adoption</div>
              <div className="metric-card__desc">Average user adoption rate across enterprise deployments at 90 days</div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Support ── */}
      <section className="support-section">
        <div className="container">
          <h2>Enterprise Support, Every Step</h2>
          <p className="support-section__sub">You're never alone — we partner with you throughout the entire journey</p>
          <div className="support-grid">
            <div className="support-card">
              <div className="support-card__icon">🤝</div>
              <div className="support-card__title">Dedicated CSM</div>
              <div className="support-card__desc">A named Customer Success Manager who knows your business and drives your implementation</div>
            </div>
            <div className="support-card">
              <div className="support-card__icon">📞</div>
              <div className="support-card__title">24/7 Technical Support</div>
              <div className="support-card__desc">Enterprise SLA with guaranteed response times and an escalation path to engineering</div>
            </div>
            <div className="support-card">
              <div className="support-card__icon">📚</div>
              <div className="support-card__title">Training &amp; Resources</div>
              <div className="support-card__desc">Live onboarding sessions, self-paced video training, and a comprehensive knowledge base</div>
            </div>
            <div className="support-card">
              <div className="support-card__icon">📈</div>
              <div className="support-card__title">Quarterly Reviews</div>
              <div className="support-card__desc">Executive business reviews with ROI reporting, roadmap updates, and strategic planning</div>
            </div>
          </div>
        </div>
      </section>

      {/* ── CTA ── */}
      <section className="hiw-cta">
        <div className="container">
          <h2>Ready to Transform Your Talent Function?</h2>
          <p>Join enterprise teams that have already seen 70% screening time reductions in their first 30 days.</p>
          <div className="cta-buttons">
            <button className="btn-primary" onClick={() => openBookDemo('Book a Demo')}>Book Your Demo</button>
            <a href="/roi-calculator" className="btn-outline">Calculate Your ROI</a>
          </div>
        </div>
      </section>

      <Footer />
    </>
  )
}
